// pothos manager «QA» v2 (хэндофф 2026-07-19) — /api/getManagerQaBoard +
// /api/setAssetVerdict. Очередь = строки каталога pothos_assets в статусе
// 'qa', разложенные по источникам (dept_photographer / dept_upwork /
// dept_scrapper / dept_it). ai-блок кадра — из check_comment (зеркалится
// work-evalом; у исторических кадров может быть пуст). Вердикты acc/rej
// для work-кадров идут через work-api reviewFrame (деньги earner'a там),
// setAssetVerdict — для остальных строк и для пометок question/recheck.
import { z } from 'zod';
import { AssetCategorySchema, AssetModalitySchema } from '../vocab/catalog';
import { QaTrainingFeedbackSchema } from './qa-training';
import { UiLangSchema } from '../vocab/ui-lang';

// Идентификатор кадра в ручках QA. До 2026-09-11 стоял max(64), а строки
// work-dev несут составные id до 114 символов: 1 731 из 1 858 скриншотов в
// очереди отвечали 400 на любой вердикт, категорию, поворот и обрезку —
// QA видела «нажал, а решение вернулось». Запас ×2 от живого максимума.
export const QaAssetIdSchema = z.string().trim().min(1).max(240);

export const PothosQaFrameSchema = z.object({
  id: z.string(),
  source: z.enum(['photo', 'upwork', 'scrape', 'it']),
  source_pipeline: z.string(),
  author: z.string(),
  merchant: z.string(),
  country: z.string(),
  country_iso: z.string(),
  locale: z.string(),
  category: z.string(),
  modality: z.string(),
  group: z.string().default(''),
  pos: z.number(),
  of: z.number(),
  ai_note: z.string(),
  uploaded_at: z.string(),
  marked: z.string(),
  thumb_url: z.string(),
  full_url: z.string(),
  // Геометрия кадра из индекса (0 = не известна). Экран проверки строит
  // рамку по пропорциям ДО загрузки картинки: без этого рамка стояла
  // 3:4 по умолчанию и прыгала, когда кадр догружался.
  width: z.number().default(0),
  height: z.number().default(0),
  // Цифровой исходник материала: код-двойник в индексе, либо оригинал пака
  // dev-отдела из work-бакета (`assetImage?id=…&src=1`). '' = исходника нет —
  // экран не показывает панель оригинала вовсе. Ставится ТОЛЬКО когда
  // исходник реально резолвится: до 2026-09-11 сюда попадал голый
  // `source_ref` скрапа (`SEC02499`) и ссылка отдавала 404.
  source_url: z.string().default(''),
  // Пак dev-отдела (id карточки из `wd-<card>-…`); '' = кадр не из пака.
  pack: z.string().default(''),
  // Исключение из капов (мигр. pothos/0128): виден на QA в закрытой ячейке и
  // проходит гейты упаковки. Ставится /api/_admin/cap_exempt.
  cap_exempt: z.boolean().default(false),
  // Устройство съёмки из индекса ('' = не определено); правится из ревью
  // через setAssetMeta (вокабуляр: iphone | other).
  device: z.string().default(''),
  // Организатор события (билеты с манифест-сверкой; юнит пачки «20 на
  // организатора» у агрегаторов) и название события — для группового
  // отклонения rejectQaGroup.
  organizer: z.string().default(''),
  event_name: z.string().default(''),
  // Держатель/участник (печать: holder_name страницы) — для «Удалить
  // участника» (rejectPrintParticipant). '' = не применимо.
  holder_name: z.string().default(''),
  // Происхождение отснятого кадра: какая страница какой печатной пачки на
  // нём снята (мигр. pothos/0097). '' / 0 = кадр не из печати.
  print_batch: z.string().default(''),
  print_page_no: z.number().default(0),
  // Прошлый вердикт кадра — для «Ревизии одобренного» и обучения новой QA.
  // Проверяющий видел только AI-заметку и не мог понять, ПОЧЕМУ кадр приняли
  // или отклонили: причина и комментарий предыдущей проверки до кабинета не
  // доезжали вообще (Натали, 10.09: «не видит причину полностью, просила
  // увидеть тикеты от предыдущей qa»). Пусто = кадр ещё не проверяли.
  prev_status: z.string().default(''),      // accepted | rejected | ''
  prev_reason: z.string().default(''),      // reject_reason из индекса
  prev_comment: z.string().default(''),     // check_comment проверяющего
  prev_by: z.string().default(''),          // dept_qa: кто вынес вердикт
  prev_at: z.string().default(''),          // checked_at
});
export type PothosQaFrame = z.infer<typeof PothosQaFrameSchema>;

// Полные счётчики очереди по источникам (агрегат по ВСЕМУ индексу, не по
// подгруженной странице кадров): especially после массовых импортов свежие
// кадры одного источника вытесняли остальные из страницы, и разбивка
// показывала нули при непустой очереди.
export const PothosQaSourceTotalsSchema = z.object({
  photo: z.number().default(0),
  upwork: z.number().default(0),
  scrape: z.number().default(0),
  it: z.number().default(0),
});
export type PothosQaSourceTotals = z.infer<typeof PothosQaSourceTotalsSchema>;

// Полная разбивка очереди по (источник, автор) — агрегат по всему индексу,
// чтобы в раскрытом источнике были видны ВСЕ люди с честными счётчиками, а не
// только авторы попавших в подгрузку кадров. Кадры конкретного автора
// догружаются тем же эндпоинтом с query source+author (frames в ответе тогда
// только этого автора).
export const PothosQaAuthorTotalSchema = z.object({
  source: z.enum(['photo', 'upwork', 'scrape', 'it']),
  author: z.string(),
  n: z.number(),
});
export type PothosQaAuthorTotal = z.infer<typeof PothosQaAuthorTotalSchema>;

// Разбивка очереди по площадкам (мерчант × страна, сырые значения индекса —
// country может нести подчёркивания) — для точечной проверки пачки одной
// площадки. Кадры площадки грузятся тем же эндпоинтом с query
// source+merchant+country.
export const PothosQaPlatformTotalSchema = z.object({
  source: z.enum(['photo', 'upwork', 'scrape', 'it']),
  merchant: z.string(),
  country: z.string(),
  n: z.number(),
});
export type PothosQaPlatformTotal = z.infer<typeof PothosQaPlatformTotalSchema>;

export const PothosQaPackTotalSchema = z.object({
  pack: z.string(),
  merchant: z.string(),
  locale: z.string(),
  country: z.string(),
  author: z.string(),
  n: z.number(),
  has_source: z.boolean(),
});
export type PothosQaPackTotal = z.infer<typeof PothosQaPackTotalSchema>;

// «Отклонённые · по проверяющим» — менеджерский режим (владелец, 2026-09-11):
// выбрать проверяющего → увидеть, из каких источников он отклонял → открыть
// любой и смотреть его отклонённое тем же экраном, что и очередь. Источник
// здесь на один шире, чем у кадра: кадры дизайнерских паков идут своей
// группой «Дизайнеры», как и на доске. Свип капов (reject_reason
// `cap_reached:…`) в счёт не идёт — это не решение человека.
export const QaRejectedSourceSchema = z.enum(['photo', 'upwork', 'scrape', 'it', 'designer']);
export type QaRejectedSource = z.infer<typeof QaRejectedSourceSchema>;
export const PothosQaRejectedTotalSchema = z.object({
  reviewer: z.string(),
  source: QaRejectedSourceSchema,
  n: z.number(),
});
export type PothosQaRejectedTotal = z.infer<typeof PothosQaRejectedTotalSchema>;

// Query того же GET /api/getManagerQaBoard в режиме отклонённого: `rejected_by`
// + `source` обязательны; `pack` или `author` сужают до пака / автора. Ответ:
// `frames` (checked_at DESC, ≤1 000), а без сужения ещё `pack_totals` и
// `author_totals` этого проверяющего в этом источнике.
export const PothosQaRejectedQuerySchema = z.object({
  rejected_by: z.string().trim().min(1).max(200),
  source: QaRejectedSourceSchema,
  pack: z.string().trim().max(400).default(''),
  author: z.string().trim().max(200).default(''),
});
export type PothosQaRejectedQuery = z.infer<typeof PothosQaRejectedQuerySchema>;

export const PothosManagerQaBoardResSchema = z.object({
  frames: z.array(PothosQaFrameSchema),
  it_screens: z.array(PothosQaFrameSchema),
  queue_total: z.number().default(0),
  source_totals: PothosQaSourceTotalsSchema.default({ photo: 0, upwork: 0, scrape: 0, it: 0 }),
  author_totals: z.array(PothosQaAuthorTotalSchema).default([]),
  platform_totals: z.array(PothosQaPlatformTotalSchema).default([]),
  // «Ревизия» — кадры с меткой «доп. проверка»: исключены из всех общих
  // очередей/счётчиков и отдаются ТОЛЬКО ревизору (QA_REVISION_EMAIL,
  // секрет воркера); остальным приходит пустой список и ноль.
  revision_frames: z.array(PothosQaFrameSchema).default([]),
  revision_total: z.number().default(0),
  // «Одобрено · по проверяющим» — счётчики status='accepted' (готово к
  // отгрузке, не отгружено) по dept_qa. Только ревизору; кадры конкретного
  // проверяющего грузятся этим же эндпоинтом с query approved_by=<email>.
  approved_totals: z.array(z.object({ reviewer: z.string(), n: z.number() })).default([]),
  // Кадры дизайнерских паков WORK-контура (source_pipeline='designer',
  // dept_designer без dept_it): исключены из общих очередей, карточка
  // «Дизайнеры» группирует их в паки по (merchant, locale) и открывает
  // обычный свайп — вердикты идут через setAssetVerdict, реворк-петля
  // work-контура их подхватывает (sweepDesignerVerdicts).
  designer_frames: z.array(PothosQaFrameSchema).default([]),
  // Паки dev-отдела в очереди «Скриншотов» — по одной строке на карточку:
  // QA открывает пак целиком и ничего кроме него (владелец, 2026-09-11).
  pack_totals: z.array(PothosQaPackTotalSchema).default([]),
  // Сколько кадров отклонил ТЕКУЩИЙ аккаунт — карточка «Отклонённые мной»
  // (владелец, 2026-09-11: «посмотреть своё отклонённое, последние сверху,
  // тот же интерфейс, можно принять»). Чужое не показывается.
  my_rejected_total: z.number().default(0),
  // Кадры-исключения в очереди (cap_exempt=1, status qa) — карточка «Исключения».
  exempt_total: z.number().default(0),
  // Сколько отклонил КАЖДЫЙ проверяющий по источникам — блок «Отклонённые ·
  // по проверяющим» (только менеджеру; роли qa фронт его не показывает).
  rejected_totals: z.array(PothosQaRejectedTotalSchema).default([]),
});
export type PothosManagerQaBoardRes = z.infer<typeof PothosManagerQaBoardResSchema>;

// POST /api/setAssetModality — ручная правка типа съёмки прямо из QA-ревью
// (photo | screenshot | photo_screenshot — третья = ФОТО экрана, встречается
// у индонезийских купонов приложений; вокабуляр старых трекеров).
export const PothosSetAssetModalityReqSchema = z.object({
  asset_id: QaAssetIdSchema,
  modality: AssetModalitySchema,
});
export type PothosSetAssetModalityReq = z.infer<typeof PothosSetAssetModalityReqSchema>;
export const PothosSetAssetModalityResSchema = z.object({ ok: z.boolean() });

// POST /api/setAssetMeta — ручная правка атрибутов кадра из QA-ревью
// (любой статус строки, как и setAssetModality): категория, устройство
// съёмки, тип съёмки. Все поля опциональны — обновляются только переданные.
export const PothosSetAssetMetaReqSchema = z.object({
  asset_id: QaAssetIdSchema,
  category: AssetCategorySchema.optional(),
  device: z.enum(['iphone', 'other']).optional(),
  modality: AssetModalitySchema.optional(),
});
export type PothosSetAssetMetaReq = z.infer<typeof PothosSetAssetMetaReqSchema>;
export const PothosSetAssetMetaResSchema = z.object({ ok: z.boolean() });

// POST /api/rejectQaGroup — «Отклонить мерчанта целиком»: снимает с QA всю
// пачку одной группы (qa + уже принятые в этой сессии). Юнит группы: у
// агрегаторов билетов — организатор (или событие, если организатора нет в
// манифесте), у обычных мерчантов — мерчант×страна внутри категории.
// merchant/country/organizer/event_name передаются СЫРЫМИ значениями индекса.
export const PothosRejectQaGroupReqSchema = z.object({
  category: z.string().trim().min(1).max(40),
  merchant: z.string().max(200),
  country: z.string().max(100),
  organizer: z.string().max(300).default(''),
  event_name: z.string().max(300).default(''),
});
export type PothosRejectQaGroupReq = z.infer<typeof PothosRejectQaGroupReqSchema>;
export const PothosRejectQaGroupResSchema = z.object({ ok: z.boolean(), rejected: z.number() });

// POST /api/replaceAssetImage — «переворот кадра» из ревью: клиент вращает
// картинку канвасом и присылает новые байты (base64 JPEG, full + thumb);
// воркер перезаписывает ОБА объекта в R2 и обновляет width/height/size/sha
// в индексе. op — для аудита, какая операция была применена.
export const PothosReplaceAssetImageReqSchema = z.object({
  asset_id: QaAssetIdSchema,
  op: z.enum(['rot90', 'rot180', 'rot270', 'flip', 'crop']),
  full_b64: z.string().min(100),
  thumb_b64: z.string().min(100),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});
export type PothosReplaceAssetImageReq = z.infer<typeof PothosReplaceAssetImageReqSchema>;
export const PothosReplaceAssetImageResSchema = z.object({ ok: z.boolean() });

// POST /api/aiRecheckAsset — кнопка «Не понимаю что это» в ревью: кадр
// уходит vision-модели (ENRICH_MODEL, Gemma 4) на переклассификацию типа
// (купон/гифт/мембершип/билет) с краткой русской заметкой; воркер применяет
// категорию/мерчанта и ставит метку «доп. проверка» — кадр уезжает в
// приватную группу «Ревизия».
export const PothosAiRecheckReqSchema = z.object({
  asset_id: QaAssetIdSchema,
});
export type PothosAiRecheckReq = z.infer<typeof PothosAiRecheckReqSchema>;
export const PothosAiRecheckResSchema = z.object({
  ok: z.boolean(),
  category: z.string().default(''),
  merchant: z.string().default(''),
  note: z.string().default(''),
});

export const PothosSetAssetVerdictReqSchema = z.object({
  asset_id: QaAssetIdSchema,
  verdict: z.enum(['acc', 'rej', 'question', 'recheck']),
  comment: z.string().max(500).default(''),
  // Язык интерфейса того, кто нажал. Нужен ТОЛЬКО тренирующемуся: разбор
  // ошибки приходит текстом из истории, и на настоящей доске это поле не
  // читается вовсе.
  lang: UiLangSchema.default('ru'),
});
export type PothosSetAssetVerdictReq = z.infer<typeof PothosSetAssetVerdictReqSchema>;
// На настоящей доске ответ — голое `{ ok }`. У тренирующегося тот же эндпоинт
// дополнительно возвращает правильный ответ и настоящую причину из истории,
// поэтому поля обратной связи здесь НЕобязательные.
export const PothosSetAssetVerdictResSchema = z.object({ ok: z.boolean() })
  .extend(QaTrainingFeedbackSchema.partial().shape);

// POST /api/setQaPackVerdict — «Отклонить весь пак» / «Принять оставшееся»
// для пака dev-отдела. rej снимает и уже принятые кадры пака (пак — единое
// целое, как rejectQaGroup); acc принимает только то, что ещё в очереди.
export const PothosQaPackVerdictReqSchema = z.object({
  pack: z.string().trim().min(4).max(400),
  verdict: z.enum(['acc', 'rej']),
  comment: z.string().max(500).default(''),
});
export type PothosQaPackVerdictReq = z.infer<typeof PothosQaPackVerdictReqSchema>;
export const PothosQaPackVerdictResSchema = z.object({ ok: z.boolean(), changed: z.number().int() });

// POST /api/setQaPackSource — оригинал для пака, у которого нет dev-карточки
// (паки агрегаторов, заведённые через intake: Groupon/Falabella/Tuango,
// 2026-09-10). Файл ложится в индекс-бакет, всем кадрам пака прописывается
// `source_ref = 'idx:<key>'`; QA видит его слева от материала.
export const PothosQaPackSourceReqSchema = z.object({
  pack: z.string().trim().min(4).max(400),
  content_type: z.enum(['image/png', 'image/jpeg', 'image/webp', 'application/pdf']),
  file_b64: z.string().min(100).max(12_000_000),
});
export type PothosQaPackSourceReq = z.infer<typeof PothosQaPackSourceReqSchema>;
export const PothosQaPackSourceResSchema = z.object({ ok: z.boolean(), linked: z.number().int() });

// POST /api/_admin/cap_exempt — пометить/снять исключение из капов. Адресат —
// список asset_id или целый пак (ключ пака доски). Заметка обязательна при
// включении: через месяц никто не вспомнит, почему эти 40 кадров прошли мимо
// закрытой ячейки.
export const PothosAdminCapExemptReqSchema = z.object({
  asset_ids: z.array(z.string().trim().min(1).max(240)).max(2000).default([]),
  pack: z.string().trim().max(400).default(''),
  on: z.boolean(),
  note: z.string().trim().max(300).default(''),
  // Исключение снимает и уже поставленный маркер cap_reached: кадр
  // возвращается из rejected в qa (только с этим маркером, ничего другого).
  restore: z.boolean().default(true),
}).refine((d) => d.asset_ids.length > 0 || d.pack, { message: 'asset_ids or pack' })
  .refine((d) => !d.on || d.note.length > 0, { message: 'note required when on' });
export type PothosAdminCapExemptReq = z.infer<typeof PothosAdminCapExemptReqSchema>;
export const PothosAdminCapExemptResSchema = z.object({ ok: z.boolean(), marked: z.number().int(), restored: z.number().int() });
